import {
  SPEAK_EASY_CATEGORIES,
  normalizeSpeakEasyCategory,
} from "../../../functions/speakEasyCategories";

export const QUESTION_MAX = 350;
export const QUESTION_PAGE_SIZES = [10, 25, 50, 100];
export const CSV_HEADERS = ["question_name", "category"];

/** Same columns as old/admin/questions-speakeasy.csv. */
const SAMPLE_CSV_ROWS = [
  ["What is one thing that always makes you smile?", "Warm-up"],
  ["What is a skill you would love to learn this year, and why?", "Explore"],
  ["From where does Veeru propose to Basanti in Sholay?", "Introspect"],
];

function csvCell(value) {
  const s = String(value ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function buildCsv(rows) {
  return [CSV_HEADERS, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
}

function downloadCsv(content, filename) {
  const blob = new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadSampleCsv() {
  downloadCsv(buildCsv(SAMPLE_CSV_ROWS), "questions-speakeasy.csv");
}

/** Old/admin/getcsv-admin.php: SELECT question_name, category ... as CSV. */
export function exportQuestionsCsv(questions) {
  downloadCsv(
    buildCsv(questions.map((q) => [q.question_name, q.category])),
    "questions.csv"
  );
}

/** RFC-4180-ish parser: quoted fields may contain commas, quotes ("") and newlines. */
function parseCsvRecords(text) {
  const records = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  const src = String(text ?? "").replace(/^﻿/, "");

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      records.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    records.push(row);
  }
  return records.filter((r) => r.some((c) => String(c).trim() !== ""));
}

/**
 * Parse an uploaded CSV into [{ question_name, category }].
 * Throws an Error with a user-facing message on any bad row, like old/admin/insertCSV.php
 * (which also requires the question_name and category header columns).
 */
export function parseQuestionsCsv(text) {
  const records = parseCsvRecords(text);
  if (!records.length) throw new Error("The CSV file is empty.");

  const headers = records[0].map((h) => String(h).trim().toLowerCase());
  const qCol = headers.findIndex((h) => h === "question_name" || h === "question");
  const cCol = headers.indexOf("category");
  if (qCol < 0 || cCol < 0) {
    throw new Error("Not matched. You must add: question_name, category columns.");
  }

  const rows = records.slice(1).map((cells, i) => {
    const rowLabel = `Row ${i + 2}`;
    const question_name = String(cells[qCol] ?? "").trim();
    if (!question_name) throw new Error(`${rowLabel}: question_name cannot be empty.`);
    if (question_name.length > QUESTION_MAX) {
      throw new Error(`${rowLabel}: question_name exceeds ${QUESTION_MAX} characters.`);
    }
    const category = normalizeSpeakEasyCategory(cells[cCol]);
    if (!category) {
      throw new Error(
        `${rowLabel}: category must be one of ${SPEAK_EASY_CATEGORIES.join(", ")}.`
      );
    }
    return { question_name, category };
  });

  if (!rows.length) throw new Error("The CSV file has no question rows.");
  return rows;
}
